import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { fetchQuery, requestSubscription, useFragment, useRelayEnvironment } from 'react-relay';

import Avatar from '@atlaskit/avatar/avatar';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { ConfluencePageAri } from '@atlassian/ari';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	PageAgentSessionNudgesQuery as PageAgentSessionNudgesQueryType,
	PageAgentSessionNudgesQuery$data,
} from './__generated__/PageAgentSessionNudgesQuery.graphql.ts';
import PageAgentSessionNudgesQuery from './__generated__/PageAgentSessionNudgesQuery.graphql.ts';
import type {
	PageAgentSessionNudgesSubscription as PageAgentSessionNudgesSubscriptionType,
	PageAgentSessionNudgesSubscription$data,
} from './__generated__/PageAgentSessionNudgesSubscription.graphql.ts';
import PageAgentSessionNudgesSubscription from './__generated__/PageAgentSessionNudgesSubscription.graphql.ts';
import type { RovoActivationFragment_query$key } from './__generated__/RovoActivationFragment_query.graphql.ts';
import type {
	RovoActivationQuery as RovoActivationQueryType,
	RovoActivationQuery$data,
} from './__generated__/RovoActivationQuery.graphql.ts';
import RovoActivationQuery from './__generated__/RovoActivationQuery.graphql.ts';
import { RovoActivationFragment } from './RovoActivationFragment.graphql.ts';

const PAGE_SIZE = 25;
const SESSION_REFRESH_DELAY_MS = 1000;

const messages = defineMessages({
	fallbackTitle: {
		id: 'page-rovo-config-common.agent-session-nudge.fallback-title.ai-non-final',
		defaultMessage: 'Rovo conversation',
		description: 'Fallback title for an unread Rovo conversation shown beside the Rovo button.',
	},
	needsInputSecondaryTitle: {
		id: 'page-rovo-config-common.agent-session-nudge.needs-input-secondary-title.ai-non-final',
		defaultMessage: 'Needs your input',
		description: 'Secondary text for a Rovo conversation that is waiting for the user to respond.',
	},
});

const DEFAULT_AGENT_SESSION_NUDGE_INVOCATION_TYPES = [
	'CONFLUENCE_COMMENT_MENTION',
	'CONFLUENCE_CHAT_EDIT',
	'CONFLUENCE_INLINE_EDIT',
	'CONFLUENCE_CWR_EDIT',
	'CONFLUENCE_CWR_CREATE',
	'CONFLUENCE_CWR_EXISTING',
	'CONFLUENCE_REMIX',
	'CONFLUENCE_REMIX_EDIT',
	'CONFLUENCE_CHAT',
] as const;

type AgentSessionNudgeState = 'INPUT_REQUIRED' | 'AUTH_REQUIRED';

const DEFAULT_AGENT_SESSION_NUDGE_STATES: readonly AgentSessionNudgeState[] = [
	'INPUT_REQUIRED',
	'AUTH_REQUIRED',
];

type AssociationQuery = NonNullable<
	PageAgentSessionNudgesQuery$data['agent_sessionAssociationQuery']
>;
export type AgentSessionAssociation = NonNullable<NonNullable<AssociationQuery['nodes']>[number]>;

type AgentSessionNudgeCandidate = Readonly<{
	agentPicture: string | null;
	channelId: string;
	channelName: string | null;
	sessionId: string;
}>;

type PageAgentSessionNudgePolicy = Readonly<{
	invocationTypes?: readonly string[];
	states?: readonly AgentSessionNudgeState[];
	unreadOnly?: boolean;
}>;

type PageAgentSessionNudgeOptions = Readonly<{
	rovoActivationFragmentRef?: RovoActivationFragment_query$key | null;
	contentId: string;
	enabled: boolean;
	policy?: PageAgentSessionNudgePolicy;
	onSessionClick?: (session: AgentSessionNudgeCandidate) => void;
}>;

type PageAgentSessionNudgeResult = Readonly<{
	nudges: readonly ConversationAction[];
	errors: readonly Error[];
}>;

// These values are interpolated inside quoted AQL literals. Escape the literal delimiters so an
// account ID can never produce malformed AQL or change the intended predicate structure.
const escapeAqlValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const buildAqlOrFilter = (field: string, values: readonly string[]): string =>
	values.map((value) => `${field} = '${escapeAqlValue(value)}'`).join('\n  OR ');

export const buildAgentSessionAql = (
	userId: string,
	pageAri: string,
	invocationTypes: readonly string[] = DEFAULT_AGENT_SESSION_NUDGE_INVOCATION_TYPES,
	states: readonly AgentSessionNudgeState[] = DEFAULT_AGENT_SESSION_NUDGE_STATES,
): string =>
	`userAccountId = '${escapeAqlValue(userId)}'\n` +
	`AND entityId = '${escapeAqlValue(pageAri)}'\n` +
	"AND deleted = 'false'\n" +
	`AND (\n  ${buildAqlOrFilter('state', states)}\n)\n` +
	`AND (\n  ${buildAqlOrFilter('invocationType', invocationTypes)}\n)`;

export const selectEligibleAgentSessions = (
	nodes: readonly (AgentSessionAssociation | null)[] | null | undefined,
	unreadOnly = true,
): AgentSessionNudgeCandidate[] => {
	// Multiple association records can resolve to the same conversation channel. Deduplicate them so
	// the same conversation is not registered as more than one nudge.
	const channelIds = new Set<string>();
	const eligible: AgentSessionNudgeCandidate[] = [];

	for (const association of nodes ?? []) {
		const session = association?.agentSession;
		const channel = session?.conversationChannel;
		// readStatus is resolved from the workspace-specific conversation channel and is not an
		// AgentSessionAssociation AQL field, so unread filtering must happen after hydration.
		if (
			!session?.id ||
			!channel?.id ||
			(unreadOnly && channel.readStatus !== false) ||
			channelIds.has(channel.id)
		) {
			continue;
		}

		channelIds.add(channel.id);
		eligible.push({
			agentPicture: session.agent?.picture ?? null,
			channelId: channel.id,
			channelName: channel.name ?? null,
			sessionId: session.id,
		});
	}

	// Preserve the server's lastStateChangeAt DESC order.
	return eligible;
};

export function usePageAgentSessionNudges({
	rovoActivationFragmentRef,
	contentId,
	enabled,
	policy,
	onSessionClick,
}: PageAgentSessionNudgeOptions): PageAgentSessionNudgeResult {
	// View Page supplies the activation data through ContentUnifiedQuery. Edit Page omits this
	// property and retains the standalone activation lookup until it has an equivalent parent query.
	const usesActivationFragment = rovoActivationFragmentRef != null;
	const { cloudId, isLoggedIn, userId } = useSessionData();
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const environment = useRelayEnvironment();
	const activationFragmentData = useFragment(
		RovoActivationFragment,
		rovoActivationFragmentRef ?? null,
	);
	const [activationQueryData, setActivationQueryData] = useState<RovoActivationQuery$data>();
	const [activationError, setActivationError] = useState<Error>();
	const [sessionData, setSessionData] = useState<PageAgentSessionNudgesQuery$data>();
	const [sessionError, setSessionError] = useState<Error>();
	const [subscriptionError, setSubscriptionError] = useState<Error>();
	// A session-change event can start a new fetch outside the initial query effect. Keep the latest
	// query subscription in a ref so a refresh can cancel an in-flight fetch and effect cleanup can
	// unsubscribe whichever fetch is current without causing another render.
	const sessionQuerySubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
	const sessionRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const invocationTypes = policy?.invocationTypes ?? DEFAULT_AGENT_SESSION_NUDGE_INVOCATION_TYPES;
	const states = policy?.states ?? DEFAULT_AGENT_SESSION_NUDGE_STATES;
	const unreadOnly = policy?.unreadOnly ?? true;

	const pageAri = useMemo(
		() =>
			cloudId && contentId
				? ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString()
				: '',
		[cloudId, contentId],
	);

	const canLoadActivation = Boolean(
		!usesActivationFragment &&
		enabled &&
		isLoggedIn &&
		userId &&
		cloudId &&
		contentId &&
		invocationTypes.length &&
		states.length &&
		fg('confluence_rovo_agent_session_nudges'),
	);

	useEffect(() => {
		if (!canLoadActivation) {
			setActivationQueryData(undefined);
			setActivationError(undefined);
			return;
		}

		const activationQuerySubscription = fetchQuery<RovoActivationQueryType>(
			environment,
			RovoActivationQuery,
			{ cloudId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				setActivationQueryData(data);
				setActivationError(undefined);
			},
			error: (error: Error) => setActivationError(error),
		});

		return () => activationQuerySubscription.unsubscribe();
	}, [canLoadActivation, cloudId, environment]);

	const activationData = usesActivationFragment ? activationFragmentData : activationQueryData;
	const rovoActivationId = activationData?.tenantContexts?.[0]?.activationIdByProduct?.active;
	const workspaceAri = rovoActivationId ? `ari:cloud:rovo::workspace/${rovoActivationId}` : '';
	const aqlQuery = useMemo(
		() =>
			userId && pageAri && invocationTypes.length && states.length
				? buildAgentSessionAql(userId, pageAri, invocationTypes, states)
				: '',
		[userId, pageAri, invocationTypes, states],
	);
	const hasAgentSessionNudgeContext = Boolean(
		enabled && isLoggedIn && aqlQuery && workspaceAri && fg('confluence_rovo_agent_session_nudges'),
	);

	const fetchSessions = useCallback(
		(isRefresh = false) => {
			if (!hasAgentSessionNudgeContext) {
				return;
			}

			sessionQuerySubscriptionRef.current?.unsubscribe();
			sessionQuerySubscriptionRef.current = fetchQuery<PageAgentSessionNudgesQueryType>(
				environment,
				PageAgentSessionNudgesQuery,
				{
					aqlQuery,
					shardingContext: workspaceAri,
					first: PAGE_SIZE,
				},
				{ fetchPolicy: isRefresh ? 'network-only' : 'store-or-network' },
			).subscribe({
				next: (data) => {
					setSessionData(data);
					setSessionError(undefined);
				},
				error: (error: Error) => setSessionError(error),
			});
		},
		[aqlQuery, environment, hasAgentSessionNudgeContext, workspaceAri],
	);

	useEffect(() => {
		if (!hasAgentSessionNudgeContext) {
			setSessionData(undefined);
			setSessionError(undefined);
			return;
		}

		// Route changes keep the View Page subtree mounted. When the page-scoped query changes,
		// clear the previous snapshot instead of retaining it while the new request is pending.
		setSessionData(undefined);
		setSessionError(undefined);
		fetchSessions();
		return () => sessionQuerySubscriptionRef.current?.unsubscribe();
	}, [fetchSessions, hasAgentSessionNudgeContext]);

	const connection = sessionData?.agent_sessionAssociationQuery;
	const eligibleSessions = useMemo(() => {
		if (!hasAgentSessionNudgeContext) {
			return [];
		}

		return selectEligibleAgentSessions(connection?.nodes, unreadOnly);
	}, [connection?.nodes, hasAgentSessionNudgeContext, unreadOnly]);

	useEffect(() => {
		if (!hasAgentSessionNudgeContext || !userId || !cloudId) {
			setSubscriptionError(undefined);
			return;
		}

		const inboxSessionChangedSubscription =
			requestSubscription<PageAgentSessionNudgesSubscriptionType>(environment, {
				subscription: PageAgentSessionNudgesSubscription,
				variables: {
					accountId: userId,
					cloudId,
					workspaceAri,
				},
				onNext: (data: PageAgentSessionNudgesSubscription$data | null | undefined) => {
					if (!data?.convoai_onInboxSessionChanged) {
						return;
					}

					setSubscriptionError(undefined);
					clearTimeout(sessionRefreshTimerRef.current);
					sessionRefreshTimerRef.current = setTimeout(() => {
						sessionRefreshTimerRef.current = undefined;
						fetchSessions(true);
					}, SESSION_REFRESH_DELAY_MS);
				},
				onError: (error: Error) => setSubscriptionError(error),
			});

		return () => {
			clearTimeout(sessionRefreshTimerRef.current);
			sessionRefreshTimerRef.current = undefined;
			inboxSessionChangedSubscription.dispose();
		};
	}, [cloudId, environment, fetchSessions, hasAgentSessionNudgeContext, userId, workspaceAri]);

	const nudges = useMemo<ConversationAction[]>(
		() =>
			eligibleSessions.map((session) => {
				const actionId = `agent-session-nudge-${session.sessionId}`;
				return {
					id: actionId,
					title: session.channelName || formatMessage(messages.fallbackTitle),
					secondaryTitle: formatMessage(messages.needsInputSecondaryTitle),
					proactiveNudgeStatus: 'needs-input',
					icon: () =>
						session.agentPicture ? (
							<Avatar appearance="hexagon" name="" size="xxsmall" src={session.agentPicture} />
						) : (
							<></>
						),
					onClick: () => {
						if (onSessionClick) {
							onSessionClick(session);
							return;
						}

						publish({
							type: 'chat-open',
							data: { channelId: session.channelId },
							openChatMode: 'mini-modal',
							source: 'confluence-agent-session-nudge',
						});
					},
					type: 'ai-action',
					handleType: HandleActionType.Base,
					nudgeContext: {
						id: actionId,
						selfEligible: true,
						suppressionKey: session.channelId,
					},
				};
			}),
		[eligibleSessions, formatMessage, onSessionClick, publish],
	);
	const errors = useMemo(
		() =>
			[activationError, sessionError, subscriptionError].filter(
				(error): error is Error => error instanceof Error,
			),
		[activationError, sessionError, subscriptionError],
	);

	return {
		nudges,
		errors,
	};
}
